/**
 * Runtime configuration.
 *
 * Values come from `window.__ENV__`, which docker-entrypoint.sh writes into env.js at container
 * start. That is what lets one built image run in every environment without a rebuild — the same
 * mechanism the AboveStore shell uses. Build-time `import.meta.env.VITE_*` is the fallback for
 * `vite dev`, and the literal defaults are the last resort.
 */

declare global {
  interface Window {
    __ENV__?: Record<string, string>
  }
}

/**
 * Resolve one configuration key.
 *
 * Exported because the module registry reads keys chosen at call time (REMOTE_REPORTING,
 * REMOTE_DASHBOARD, …) rather than a fixed set known here, and it must see the same precedence:
 * runtime `window.__ENV__` first, then the build-time value, then the fallback.
 */
export function env(key: string, viteValue: string | undefined, fallback: string): string {
  if (typeof window !== 'undefined' && window.__ENV__?.[key]) return window.__ENV__[key]
  return viteValue ?? fallback
}

/**
 * The identity provider's realm as the browser sees it — normally this portal's own origin plus
 * `/auth/realms/relay`, which nginx proxies to the provider (Plans/identity-consolidation.md).
 *
 * Every person signs in there; there is no other sign-in to fall back to. Empty means the deployment
 * is misconfigured, and the portal says so rather than redirecting nowhere.
 */
export const OIDC_AUTHORITY = env('OIDC_AUTHORITY', import.meta.env.VITE_OIDC_AUTHORITY, '')

/** Public PKCE client for this portal. */
export const OIDC_CLIENT_ID = env(
  'OIDC_CLIENT_ID',
  import.meta.env.VITE_OIDC_CLIENT_ID,
  'relay-portal',
)

/**
 * Base path for Relay API calls. Same-origin by default: nginx proxies /api to relay-server:8082,
 * which keeps the browser on one origin and avoids both CORS and mixed content.
 */
export const API_BASE = env('API_BASE', import.meta.env.VITE_API_BASE, '/api')

/**
 * WebSocket base for viewer sessions. Empty means "derive from the page origin", which is what
 * the Blazor client did; a device's own relay URL from the discovery endpoint takes precedence
 * over this at connect time.
 */
export const WS_BASE = env('WS_BASE', import.meta.env.VITE_WS_BASE, '')

/**
 * The base map style the device map opens with.
 *
 * Defaults to the OpenFreeMap style the Blazor portal uses. It is configurable because a relay
 * deployed inside a store estate often has no route to the public internet, and the map is the one
 * page that degrades to nothing without tiles — pointing this at a self-hosted tile server is then
 * an environment variable rather than a rebuild.
 */
export const MAP_STYLE_URL = env(
  'MAP_STYLE_URL',
  import.meta.env.VITE_MAP_STYLE_URL,
  'https://tiles.openfreemap.org/styles/liberty',
)

/** The street map in the dark theme: a bright beige map inside a dark portal was the loudest thing on the page. */
export const MAP_STYLE_URL_DARK = env(
  'MAP_STYLE_URL_DARK',
  import.meta.env.VITE_MAP_STYLE_URL_DARK,
  'https://tiles.openfreemap.org/styles/dark',
)

/** Derive a ws(s):// origin from the page, for when discovery returns no relay URL. */
export function defaultWebSocketBase(): string {
  if (WS_BASE) return WS_BASE
  if (typeof window === 'undefined') return ''
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}`
}
